// @file Analytics helper for the landside marketing pages (the activation → sign-up funnel).
//
// Wraps `trackEvent` (bits/analytics) so every landside event carries a consistent
// `category` and a `page` property, and dual-emits each event to the main Padlet PostHog
// project (see `captureLandsidePostHogEvent`) so funnels can be built there without waiting
// on the warehouse. Two problems the wrapper solves:
//   1. Today the real homepage, all five landing pages, and most of their CTAs collide
//      under the single category `'Homepage'`, so per-page funnels are impossible.
//   2. Event names drift per page ('Sandbox Landside', 'Landside template gallery', …),
//      so cross-page funnels can't join.
// The wrapper produces the category in exactly one place and always injects `page`, so
// callers cannot reintroduce that drift.
//
// Two known funnel gaps are tracked elsewhere — do not re-scope them here:
//   - Anonymous→identified stitching (client `identify()`/`.alias()` + a 'Signed up'
//     event) is MAR-582; without it the funnel ends at "signup step submitted".
//   - Delivery reliability: `trackEvent` no-ops before `window.load` and click-then-navigate
//     can drop the beacon, so CTA clicks undercount relative to impressions. The fix lives
//     in the shared bootstrap (_pepin_analytics.html.erb) and pulls against ENG-40868
//     (defer analytics off the homepage boot path) — coordinate with that ticket's owner.
import { trackEvent } from '@@/bits/analytics'
import { captureException } from '@@/bits/error_tracker'
import type { LandsidePage } from '@@/pinia/landside_navigation'
import { usePostHog } from '@@/vuecomposables/posthog'

// Only the five funnel landing pages get a dedicated `Landside <Page>` category. Every
// other landside route (home, product, pricing, dynamic landing pages, arcade) keeps the
// historical `'Homepage'` category so we don't silently recategorize non-funnel traffic —
// the precise page is always available in the `page` property regardless.
const LANDSIDE_EVENT_CATEGORY_BY_PAGE: Partial<Record<LandsidePage, string>> = {
  board: 'Landside Board',
  sandbox: 'Landside Sandbox',
  subscriptions: 'Landside Subscriptions',
  templates: 'Landside Templates',
  education: 'Landside Education',
}
const DEFAULT_LANDSIDE_CATEGORY = 'Homepage'

// Where on the page an event originated. Bounded set → low cardinality, easy to group.
export enum LandsideSection {
  Header = 'header',
  Hero = 'hero',
  Pricing = 'pricing',
  Faq = 'faq',
  Gallery = 'gallery',
  Examples = 'examples',
  Testimonials = 'testimonials',
  Footer = 'footer',
  // The nav side panel only renders on mobile-phone-portrait viewports (the hamburger menu),
  // so the slug says so — a bare 'nav' reads as if it could be the desktop tab bar.
  MobilePhoneNav = 'mobile_phone_nav',
}

// Which call-to-action fired. New conversion clicks all use the action `'Clicked CTA'` and
// are disambiguated by this slug, so the funnel key is `cta` (not a per-button action string).
export enum LandsideCta {
  MakeBoard = 'make_board',
  MakeSandbox = 'make_sandbox',
  SignUp = 'sign_up',
  LogIn = 'log_in',
  GetQuote = 'get_quote',
  SelectPlan = 'select_plan',
  ComparePlans = 'compare_plans',
  ViewPlans = 'view_plans',
  ViewExample = 'view_example',
  UseTemplate = 'use_template',
}

// The generic action every new landside conversion click uses (disambiguated by `cta`).
export const LANDSIDE_CLICK_CTA_ACTION = 'Clicked CTA'
// The action every landside section-impression uses (disambiguated by `section`).
export const LANDSIDE_VIEW_SECTION_ACTION = 'Viewed section'

export interface LandsideEventProps {
  section?: LandsideSection
  cta?: LandsideCta
  destination?: string
  loggedIn?: boolean
  plan?: string
  [key: string]: unknown
}

/**
 * Kick off the lazy PostHog client load ahead of the first capture. The client lives in a
 * dynamically imported chunk; if that import only starts inside a CTA click handler, the
 * page unloads (CTA clicks navigate immediately) before the chunk arrives and the capture
 * is silently lost. Instrumented surfaces call this on mount so the client is ready by the
 * time a click can happen. Safe to call repeatedly — usePostHog caches one init per page
 * load and retries after a failed one.
 */
export function warmLandsidePostHog(): void {
  usePostHog().catch((error) => {
    captureException(error, { context: { source: 'landside_analytics', event: 'warm_posthog' } })
  })
}

/**
 * Capture a landside event into the main Padlet PostHog project (the same client the NPS
 * survey and session recording use — not the arcade instance in bits/posthog.ts). Every
 * landside event dual-emits: RudderStack keeps the existing GTM/warehouse pipeline intact,
 * PostHog gets the same taxonomy for product analytics. The PostHog event name is the
 * Rudder `action` verbatim and the Rudder `category` rides along as a property, so the two
 * destinations stay joinable on identical `page`/`section`/`cta` values.
 *
 * Fire-and-forget: the client inits lazily on first capture, so surfaces whose events race
 * a navigation must warm it up front (see warmLandsidePostHog). A failure must never break
 * the calling UI — but it is reported, not swallowed.
 */
export function captureLandsidePostHogEvent(event: string, properties: Record<string, unknown>): void {
  usePostHog()
    .then(({ posthog }) => {
      // Beacon + skip-the-batch: most landside CTAs navigate away immediately, and a queued
      // XHR/fetch dies with the document. A beacon survives the unload.
      posthog.capture(event, properties, { transport: 'sendBeacon', send_instantly: true })
    })
    .catch((error) => {
      captureException(error, { context: { source: 'landside_analytics', event } })
    })
}

/**
 * Track a landside event. Injects a stable `page` property and derives the category from
 * the page, so cross-page funnels join on `page`/`section`/`cta` instead of brittle
 * category strings. `label`/`value` are reserved for the few existing high-cardinality
 * human strings (template title, search query) — never emails, user ids, or full hrefs.
 */
export function trackLandsideEvent(
  page: LandsidePage,
  action: string,
  props: LandsideEventProps = {},
  label: string | null = null,
  value: number | null = null,
): void {
  const category = LANDSIDE_EVENT_CATEGORY_BY_PAGE[page] ?? DEFAULT_LANDSIDE_CATEGORY
  // PII safety is enforced here, not opt-in per caller: any `destination` is normalized
  // before it reaches either pipeline.
  const safeProps =
    props.destination != null ? { ...props, destination: normalizeDestination(props.destination) } : props
  // Injected keys are spread last so a stray `page`/`category` in props can never override
  // the centrally derived values — that would silently reintroduce the drift this module
  // exists to prevent.
  trackEvent(category, action, label, value, { ...safeProps, page })
  captureLandsidePostHogEvent(action, {
    ...safeProps,
    ...(label != null && { label }),
    ...(value != null && { value }),
    category,
    page,
  })
}

// Path segments that are known static route words on landside CTA destinations. Anything
// NOT in this vocabulary is treated as a dynamic — and therefore potentially identifying —
// segment and collapsed to `:id`. The allowlist fails closed: a username, wall slug, board
// id, or email in a path can never reach analytics just because it doesn't look "id-like".
// Extend the set when a CTA legitimately targets a new static route.
const STATIC_DESTINATION_SEGMENTS = new Set([
  'auth',
  'signup',
  'login',
  'quick-create',
  'site',
  'board',
  'sandbox',
  'subscriptions',
  'templates',
  'education',
  'product',
  'pricing',
  'features',
  'account',
  'create',
  'classroom',
  'premium',
  'upgrade',
  'dashboard',
  'gallery',
  'whatsnew',
  'help',
  'careers',
  'referrals',
])

// Sentinel for destinations that carry no safe path: mailto:/tel:/other schemes and
// protocol-relative URLs (whose "path" starts with a foreign host).
const EXTERNAL_DESTINATION = ':external'

/**
 * Reduce a CTA href to a low-cardinality, PII-free `destination`. Paths keep only
 * allowlisted static route segments — query and hash are dropped, the origin of absolute
 * http(s) URLs is dropped, and every non-allowlisted segment collapses to `:id` (e.g.
 * `/premium/upgrade/gold-1yr` → `/premium/upgrade/:id`). Scheme'd non-http destinations
 * (mailto:, tel:, …) and protocol-relative URLs reduce to the `:external` sentinel, so
 * emails, phone numbers, and hosts never reach analytics.
 */
export function normalizeDestination(href: string): string {
  if (!href) return ''
  let path = href
  if (/^https?:\/\//i.test(path)) {
    // Absolute URL: drop the origin (host included), keep the path for the allowlist below.
    path = path.replace(/^https?:\/\/[^/]*/i, '')
  } else if (path.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(path)) {
    return EXTERNAL_DESTINATION
  }
  const normalized = path
    .split(/[?#]/)[0]
    .split('/')
    .map((segment) => {
      if (segment === '') return segment
      const lowered = segment.toLowerCase()
      return STATIC_DESTINATION_SEGMENTS.has(lowered) ? lowered : ':id'
    })
    .join('/')
  return normalized || '/'
}
